import React, { useEffect, useMemo, useRef, useState } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import UnfoldLessOutlinedIcon from '@mui/icons-material/UnfoldLessOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { cooperplaceApi } from 'services/api';
import Lightbox from 'components/Lightbox';
import { styles } from './styles';

function formatDateBR(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function formatDateTimeBR(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getResponseScore(response) {
  return (
    response?.score || {
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalAnswers: 0,
      percentage: 0,
      answeredQuestions: 0,
      answeredPercentage: 0,
    }
  );
}

function hasQuestionAnswer(question) {
  const selectedResponses = Array.isArray(question?.responses)
    ? question.responses.filter(response => response?.selected)
    : [];
  const hasDescriptiveResponse = !!String(
    question?.questionDescriptiveResponse || ''
  ).trim();
  const hasAttachments =
    Array.isArray(question?.attachments) && question.attachments.length > 0;
  const isVideoVisualization =
    getQuestionTypeSlug(question) === 'video_visualization';

  return (
    selectedResponses.length > 0 ||
    hasDescriptiveResponse ||
    hasAttachments ||
    isVideoVisualization
  );
}

function formatScoreValue(value, options = {}) {
  return Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  });
}

function getResponseIdentity(response) {
  const user = response?.user || {};

  return {
    name: user?.username || 'Não informado',
    email: user?.email || 'Não informado',
    cpf: user?.cpf || 'Não informado',
    phone: user?.phoneNumber || 'Não informado',
  };
}

function getCertificateUrl(response) {
  return (
    response?.certificate?.mediaUrl ||
    response?.certificate?.media_url ||
    response?.certificate?.certificateUrl ||
    response?.certificate?.certificate_url ||
    response?.certificate?.url ||
    response?.certificate?.fullPath ||
    response?.certificate?.full_path ||
    response?.certificate?.mediaFullPath ||
    response?.certificate?.media_full_path ||
    response?.certificate?.media?.fullPath ||
    response?.certificate?.media?.full_path ||
    response?.certificate?.media?.url ||
    response?.certificateMediaUrl ||
    response?.certificate_media_url ||
    ''
  );
}

function isCertificateEligible(score) {
  return Number(score?.percentage || 0) >= 70;
}

function getQuestionTypeSlug(question) {
  return String(question?.type?.slug || '').toLowerCase();
}

function getQuestionAnswer(question) {
  const selectedResponses = Array.isArray(question?.responses)
    ? question.responses
        .filter(response => response?.selected)
        .map(response => response?.description)
        .filter(Boolean)
    : [];

  if (selectedResponses.length) return selectedResponses.join(', ');
  if (question?.questionDescriptiveResponse) {
    return question.questionDescriptiveResponse;
  }

  const attachments = Array.isArray(question?.attachments)
    ? question.attachments
    : [];
  if (attachments.length) {
    return `${attachments.length} anexo(s) enviado(s)`;
  }

  if (getQuestionTypeSlug(question) === 'video_visualization') {
    return 'Visualização de vídeo concluída';
  }

  return '—';
}

function getQuestionStatus(question) {
  const evaluation = getQuestionEvaluation(question);
  const isVideoVisualization =
    getQuestionTypeSlug(question) === 'video_visualization';

  if (isVideoVisualization) {
    return null;
  }

  if (evaluation.isPerfect) {
    return { label: 'Correta', tone: 'success' };
  }

  if (evaluation.hasError) {
    return { label: 'Incorreta', tone: 'danger' };
  }

  if (evaluation.hasCorrectDefinition && evaluation.selected.length === 0) {
    return { label: 'Não respondido', tone: 'neutral' };
  }

  const hasDescriptiveResponse = !!question?.questionDescriptiveResponse;
  const hasAttachments =
    Array.isArray(question?.attachments) && question.attachments.length > 0;
  const hasAnyAnswer =
    evaluation.selected.length > 0 || hasDescriptiveResponse || hasAttachments;

  if (!hasAnyAnswer) {
    return { label: 'Não respondido', tone: 'neutral' };
  }

  return null;
}

function getGroupQuestions(responseItem) {
  return Array.isArray(responseItem?.questions) ? responseItem.questions : [];
}

function getComputedResponseScore(responseItem) {
  const backendScore = getResponseScore(responseItem);
  const questions = getGroupQuestions(responseItem);

  if (!questions.length) {
    return backendScore;
  }

  const answeredQuestions = questions.filter(hasQuestionAnswer).length;
  const scorableQuestions = questions.filter(
    question => getQuestionEvaluation(question).hasCorrectDefinition
  );
  const correctAnswers = scorableQuestions.filter(
    question => getQuestionEvaluation(question).isPerfect
  ).length;
  const totalAnswers = scorableQuestions.length;
  const incorrectAnswers = Math.max(totalAnswers - correctAnswers, 0);
  const percentage =
    totalAnswers > 0 ? (correctAnswers / totalAnswers) * 100 : 0;
  const answeredPercentage =
    questions.length > 0 ? (answeredQuestions / questions.length) * 100 : 0;

  return {
    ...backendScore,
    correctAnswers,
    incorrectAnswers,
    totalAnswers,
    percentage,
    answeredQuestions,
    answeredPercentage,
  };
}

function normalizeUrl(url) {
  return String(url || '').toLowerCase().split('?')[0].split('#')[0];
}

function isImageUrl(url) {
  const lower = normalizeUrl(url);
  return (
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.webp')
  );
}

function isVideoUrl(url) {
  const lower = normalizeUrl(url);
  return (
    lower.endsWith('.mp4') ||
    lower.endsWith('.webm') ||
    lower.endsWith('.mov') ||
    lower.endsWith('.m4v') ||
    lower.endsWith('.avi')
  );
}

function isPdfUrl(url) {
  return normalizeUrl(url).endsWith('.pdf');
}

function getAttachmentUrl(att) {
  return (
    att?.attachment?.full_path ||
    att?.attachment?.url ||
    att?.attachment?.publicUrl ||
    att?.attachment?.link ||
    att?.attachment?.path ||
    att?.full_path ||
    att?.url ||
    att?.link ||
    ''
  );
}

function getAttachmentName(att, index) {
  return att?.attachment?.name || att?.name || `Arquivo ${index + 1}`;
}

function getQuestionEvaluation(question) {
  const responseOptions = Array.isArray(question?.responses) ? question.responses : [];
  const hasCorrectAnswer = !!(
    question?.originalQuestion?.hasCorrectAnswer ?? question?.hasCorrectAnswer
  );
  const originalOptions =
    (Array.isArray(question?.originalQuestion?.values) &&
      question.originalQuestion.values) ||
    (Array.isArray(question?.values) && question.values) ||
    [];

  const options = (originalOptions.length ? originalOptions : responseOptions).map(
    option => {
      const matchedResponse = responseOptions.find(
        response =>
          (response?.id && option?.id && response.id === option.id) ||
          String(response?.description || '').trim().toLowerCase() ===
            String(option?.description || '').trim().toLowerCase()
      );

      return {
        ...option,
        selected: !!matchedResponse?.selected,
        responseId: matchedResponse?.id,
      };
    }
  );

  const selected = options.filter(item => !!item?.selected);
  const correct = hasCorrectAnswer
    ? options.filter(item => item?.isCorrect === true)
    : [];
  const hasCorrectDefinition = hasCorrectAnswer && correct.length > 0;
  const incorrectSelected = hasCorrectDefinition
    ? selected.filter(item => item?.isCorrect === false)
    : [];
  const missedCorrect = hasCorrectDefinition
    ? correct.filter(
        correctItem =>
          !selected.find(selectedItem => selectedItem?.id === correctItem?.id)
      )
    : [];
  const hasMultipleCorrect = correct.length > 1;
  const isPerfect =
    hasCorrectDefinition &&
    incorrectSelected.length === 0 &&
    missedCorrect.length === 0 &&
    selected.length > 0;
  const hasError = incorrectSelected.length > 0;
  const isPartial = hasMultipleCorrect && !isPerfect;

  return {
    options,
    selected,
    correct,
    incorrectSelected,
    missedCorrect,
    hasCorrectDefinition,
    hasMultipleCorrect,
    isPerfect,
    hasError,
    isPartial,
  };
}

function isCorrectOption(option) {
  return option?.isCorrect === true;
}

function getOptionVisualState(option, evaluation) {
  const isSelected = !!option?.selected;
  const isCorrect = isCorrectOption(option);

  if (!evaluation.hasCorrectDefinition) {
    return isSelected ? 'info' : 'neutral';
  }

  if (isSelected && isCorrect && evaluation.hasMultipleCorrect && !evaluation.isPerfect) {
    return 'warning';
  }

  if (isSelected && isCorrect) return 'success';
  if (isSelected && !isCorrect) return 'danger';
  if (!isSelected && isCorrect && evaluation.hasMultipleCorrect && !evaluation.isPerfect) {
    return 'danger';
  }
  if (!isSelected && isCorrect && evaluation.hasMultipleCorrect) return 'warning';
  if (!isSelected && isCorrect) return 'successMuted';
  return 'neutral';
}

function getOptionStyles(state) {
  return styles.optionToneStyles[state] || styles.optionToneStyles.neutral;
}

function toneStyles(tone) {
  return styles.statusToneStyles[tone] || styles.statusToneStyles.neutral;
}

const RESPONSE_VALIDITY_DAYS = 365;

function getResponseExpiryDate(responseItem) {
  const answeredAt = toDate(responseItem?.answeredAt);

  if (!answeredAt) return null;

  const expiresAt = new Date(answeredAt);
  expiresAt.setDate(expiresAt.getDate() + RESPONSE_VALIDITY_DAYS);
  return expiresAt;
}

function isResponseActive(responseItem) {
  const answeredAt = toDate(responseItem?.answeredAt);
  const expiresAt = getResponseExpiryDate(responseItem);

  if (!answeredAt || !expiresAt) {
    return !!responseItem?.isActive;
  }

  return expiresAt.getTime() >= Date.now();
}

function getScoreMeta(score) {
  const percentage = Number(score?.percentage || 0);
  const totalAnswers = Number(score?.totalAnswers || 0);
  const answeredPercentage = Number(score?.answeredPercentage || 0);

  if (totalAnswers === 0 && answeredPercentage >= 100) {
    return styles.scoreMeta.success;
  }

  if (totalAnswers === 0) {
    return styles.scoreMeta.neutral || styles.scoreMeta.warning;
  }

  if (percentage >= 100) {
    return styles.scoreMeta.success;
  }

  if (percentage >= 60) {
    return styles.scoreMeta.warning;
  }

  return styles.scoreMeta.danger;
}

function buildStatusMeta(responseItem) {
  const expiresAt = getResponseExpiryDate(responseItem);

  if (isResponseActive(responseItem)) {
    let label = 'Ativo';

    if (expiresAt) {
      const diffMs = expiresAt.getTime() - Date.now();
      const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays > 0) {
        label = `Ativo • Vence em ${diffDays} dias`;
      }
    }

    return {
      label,
      styles: styles.responseStatusMeta.active,
    };
  }

  let expiredLabel = 'Vencido';

  if (expiresAt) {
    const diffMs = Date.now() - expiresAt.getTime();
    const diffDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    expiredLabel = `Vencido há ${diffDays} dias`;
  }

  return {
    label: expiredLabel,
    styles: styles.responseStatusMeta.expired,
  };
}

function buildInfoCard(icon, label, value) {
  return { icon, label, value };
}

function getResponseKey(responseItem, index) {
  return String(responseItem?.id ?? responseItem?.group ?? index);
}

const INITIAL_LIGHTBOX_STATE = {
  isOpen: false,
  items: [],
  index: 0,
};

const INITIAL_PREVIEW_STATE = {
  isOpen: false,
  url: '',
  name: '',
  kind: '',
  blobUrl: '',
  loading: false,
  error: '',
};

export default function TrainingAnsweredResponses({
  open = false,
  onClose,
  singleChecklistId = null,
  checklistTitle = '',
}) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState([]);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    active: true,
    inactive: true,
  });
  const [expandedMap, setExpandedMap] = useState({});
  const [exportingId, setExportingId] = useState(null);
  const [lightbox, setLightbox] = useState(INITIAL_LIGHTBOX_STATE);
  const [preview, setPreview] = useState(INITIAL_PREVIEW_STATE);
  const previewVideoRef = useRef(null);

  useEffect(() => {
    if (open) return;

    setLightbox(prev => {
      if (!prev.isOpen && prev.items.length === 0 && prev.index === 0) {
        return prev;
      }

      return INITIAL_LIGHTBOX_STATE;
    });

    setPreview(prev => {
      if (
        !prev.isOpen &&
        !prev.url &&
        !prev.name &&
        !prev.kind &&
        !prev.blobUrl &&
        !prev.loading &&
        !prev.error
      ) {
        return prev;
      }

      if (prev.blobUrl) {
        try {
          URL.revokeObjectURL(prev.blobUrl);
        } catch (error) {
          // noop
        }
      }

      return INITIAL_PREVIEW_STATE;
    });
  }, [open]);

  useEffect(() => {
    if (!open || !singleChecklistId) return;

    async function fetchTrainingResponses() {
      try {
        setLoading(true);
        const response = await cooperplaceApi.get(
          `/training-checklist/${singleChecklistId}`
        );
        const payload = response?.data?.data || response?.data || null;
        const answeredResponses = Array.isArray(payload?.questionsResponses)
          ? payload.questionsResponses.filter(item => !!item?.answeredAt)
          : [];

        setData(payload);
        setResponses(answeredResponses);
        setExpandedMap(
          Object.fromEntries(
            answeredResponses.map((item, index) => [
              getResponseKey(item, index),
              false,
            ])
          )
        );
      } catch (e) {
        setData(null);
        setResponses([]);
        setExpandedMap({});
      } finally {
        setLoading(false);
      }
    }

    fetchTrainingResponses();
  }, [open, singleChecklistId]);

  const filteredResponses = useMemo(() => {
    const normalizedSearch = String(search || '')
      .trim()
      .toLowerCase();

    return (responses || []).filter((response, index) => {
      const identity = getResponseIdentity(response);
      const isActive = isResponseActive(response);
      const matchesStatus = (isActive && filters.active) || (!isActive && filters.inactive);

      if (!matchesStatus) return false;
      if (!normalizedSearch) return true;

      const haystack = [
        identity.name,
        identity.email,
        identity.cpf,
        identity.phone,
        String(response?.group ?? index + 1),
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [responses, search, filters, data]);

  if (!open) return null;

  async function handleExport(responseItem, index) {
    const exportId =
      responseItem?.id ||
      responseItem?.trainingChecklistId ||
      responseItem?.singleChecklistId ||
      singleChecklistId;
    const exportGroup = Number(responseItem?.group);

    if (!exportId) return;

    try {
      setExportingId(String(responseItem?.id ?? index));
      const response = await cooperplaceApi.get(
        `/training-checklist/${exportId}/export`,
        {
          params: Number.isFinite(exportGroup) ? { group: exportGroup } : undefined,
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/pdf',
          },
        }
      );

      const checklistTitle = String(
        data?.title || data?.name || data?.checklistTitle || 'Treinamento'
      )
        .trim()
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '');
      const responseNumber = Number(responseItem?.group) || index + 1;
      const fileName = `${checklistTitle}_Resp.${responseNumber}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } finally {
      setExportingId(null);
    }
  }

  function handleDownloadCertificate(responseItem) {
    const certificateUrl = getCertificateUrl(responseItem);

    if (!certificateUrl) return;

    window.open(certificateUrl, '_blank', 'noopener,noreferrer');
  }

  function toggleExpand(key) {
    setExpandedMap(prev => ({
      ...prev,
      [key]: !prev?.[key],
    }));
  }

  function setAllExpanded(value) {
    const next = {};
    filteredResponses.forEach((item, index) => {
      next[getResponseKey(item, index)] = value;
    });
    setExpandedMap(prev => ({ ...prev, ...next }));
  }

  function toggleFilter(key) {
    setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function downloadFile(url, filename) {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'arquivo';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  function closePreview() {
    setPreview(prev => {
      if (prev?.blobUrl) {
        try {
          URL.revokeObjectURL(prev.blobUrl);
        } catch (error) {
          // noop
        }
      }

      return {
        ...INITIAL_PREVIEW_STATE,
      };
    });
  }

  function openPreviewFullscreen() {
    const videoEl = previewVideoRef.current;
    if (!videoEl) return;

    if (videoEl.requestFullscreen) {
      videoEl.requestFullscreen();
      return;
    }

    if (videoEl.webkitRequestFullscreen) {
      videoEl.webkitRequestFullscreen();
      return;
    }

    if (videoEl.webkitEnterFullscreen) {
      videoEl.webkitEnterFullscreen();
    }
  }

  async function openPreview(url, name) {
    if (!url) return;

    const kind = isPdfUrl(url) ? 'pdf' : isVideoUrl(url) ? 'video' : 'file';
    setPreview({
      isOpen: true,
      url,
      name,
      kind,
      blobUrl: '',
      loading: kind === 'pdf',
      error: '',
    });

    if (kind === 'pdf') {
      try {
        const resp = await fetch(url);

        if (!resp.ok) {
          throw new Error('pdf_preview_failed');
        }

        const sourceBlob = await resp.blob();
        const pdfBlob = new Blob([sourceBlob], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(pdfBlob);
        setPreview(prev => ({ ...prev, blobUrl, loading: false }));
      } catch (error) {
        setPreview(prev => ({
          ...prev,
          loading: false,
          blobUrl: '',
          error: 'Não foi possível abrir o PDF para visualização.',
        }));
      }
    }
  }

  function openLightboxFromAttachments({ attachments, currentUrl }) {
    const items = (attachments || [])
      .map((att, index) => ({
        url: getAttachmentUrl(att),
        name: getAttachmentName(att, index),
      }))
      .filter(item => !!item.url && isImageUrl(item.url));

    if (!items.length) return;

    const index = items.findIndex(item => item.url === currentUrl);
    setLightbox({
      isOpen: true,
      items,
      index: index >= 0 ? index : 0,
    });
  }

  function renderMediaGrid(attachments = []) {
    const safeAttachments = Array.isArray(attachments) ? attachments : [];
    if (!safeAttachments.length) return null;

    return (
      <div style={styles.mediaGrid}>
        {safeAttachments.map((att, index) => {
          const url = getAttachmentUrl(att);
          const name = getAttachmentName(att, index);

          if (!url) {
            return (
              <div key={index} style={styles.attachmentFallbackCard}>
                <InsertDriveFileOutlinedIcon style={{ fontSize: 24, color: '#6B7280' }} />
                <div style={styles.attachmentFallbackName}>{name}</div>
              </div>
            );
          }

          if (isImageUrl(url)) {
            return (
              <button
                key={index}
                type="button"
                onClick={() =>
                  openLightboxFromAttachments({ attachments: safeAttachments, currentUrl: url })
                }
                style={{ ...styles.mediaButtonBase, ...styles.imageButton }}
              >
                <div
                  style={{
                    ...styles.imageThumb,
                    backgroundImage: `url(${url})`,
                  }}
                />
                <div style={{ ...styles.mediaName, ...styles.imageName }}>
                  {name}
                </div>
              </button>
            );
          }

          if (isVideoUrl(url)) {
            return (
              <button
                key={index}
                type="button"
                onClick={() => openPreview(url, name)}
                style={{ ...styles.mediaButtonBase, ...styles.videoButton }}
              >
                <video
                  src={url}
                  muted
                  playsInline
                  preload="metadata"
                  style={styles.videoThumb}
                />
                <div style={styles.mediaName}>
                  {name}
                </div>
              </button>
            );
          }

          return (
            <button
              key={index}
              type="button"
              onClick={() => openPreview(url, name)}
              style={styles.fileButton}
            >
              <InsertDriveFileOutlinedIcon style={{ fontSize: 24, color: '#6B7280' }} />
              <div style={styles.fileName}>
                {name}
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  const title = checklistTitle || data?.name || 'Treinamento';
  const createdBy = data?.createdUser?.username || '—';
  const createdAt = formatDateBR(data?.createdAt);
  const totalResponses = responses.length;
  const allExpanded =
    filteredResponses.length > 0 &&
    filteredResponses.every((item, index) => {
      const key = getResponseKey(item, index);
      return !!expandedMap[key];
    });

  const infoPills = [
    buildInfoCard(
      <PersonOutlineOutlinedIcon style={{ fontSize: 18, color: '#9CA3AF' }} />,
      'Criado por',
      createdBy
    ),
    buildInfoCard(
      <CalendarTodayOutlinedIcon style={{ fontSize: 18, color: '#9CA3AF' }} />,
      '',
      createdAt
    ),
    buildInfoCard(
      <ImageOutlinedIcon style={{ fontSize: 18, color: '#9CA3AF' }} />,
      '',
      `${totalResponses} resposta(s)`
    ),
  ];

  function renderQuestionCard(question, idx) {
    const statusMeta = getQuestionStatus(question);
    const evaluation = getQuestionEvaluation(question);
    const isVideoVisualization = getQuestionTypeSlug(question) === 'video_visualization';
    const options = Array.isArray(evaluation?.options) ? evaluation.options : [];
    const hasSelectableOptions = options.length > 0;
    const selectedOptions = evaluation.selected.filter(item => !!item?.description);
    const correctDescriptions = evaluation.correct.map(item => item?.description).filter(Boolean);
    const hasAttachments = Array.isArray(question?.attachments) && question.attachments.length > 0;

    return (
      <div key={question?.id || idx} style={styles.questionCard}>
        <div style={styles.questionHeader}>
          <div style={styles.questionTitle}>
            {idx + 1}. {question?.question || 'Pergunta'}
          </div>

          {statusMeta && (
            <span
              style={{
                ...toneStyles(statusMeta.tone),
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                height: 28,
                padding: '0 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {statusMeta.label}
            </span>
          )}
        </div>

        <div style={styles.questionDivider} />

        {hasSelectableOptions ? (
          <div style={styles.optionsList}>
            {options.map((option, optionIndex) => {
              const state = getOptionVisualState(option, evaluation);
              const optionStyles = getOptionStyles(state);
              const isSelected = !!option?.selected;
              const isCorrect = isCorrectOption(option);

              return (
                <div
                  key={option?.id || optionIndex}
                  style={{
                    ...optionStyles,
                    ...styles.optionCard,
                  }}
                >
                  <div
                    style={{
                      ...styles.optionLabel,
                      fontWeight: isSelected ? 700 : 500,
                    }}
                  >
                    {option?.description || 'Opcao'}
                  </div>

                  <div style={styles.optionMeta}>
                    {isSelected && (
                      <span style={styles.optionTag}>
                        Resposta marcada
                      </span>
                    )}
                    {!isSelected && isCorrect && (
                      <span style={styles.optionTag}>
                        Resposta correta
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              ...styles.questionAnswerBox,
              ...(isVideoVisualization ? styles.optionToneStyles.success : {}),
              ...(question?.questionDescriptiveResponse ? styles.optionToneStyles.info : {}),
            }}
          >
            {getQuestionAnswer(question)}
          </div>
        )}

        {!hasSelectableOptions && !!selectedOptions.length && (
          <div style={styles.questionSection}>
            <div style={styles.questionSectionTitle}>
              Respostas marcadas pelo usuario
            </div>
            <div style={styles.chipsRow}>
              {selectedOptions.map((item, index) => {
                const chipStyles = !evaluation.hasCorrectDefinition
                  ? styles.optionToneStyles.info
                  : isCorrectOption(item)
                  ? styles.optionToneStyles.success
                  : styles.optionToneStyles.danger;

                return (
                  <span
                    key={`${question?.id || idx}-selected-${index}`}
                    style={{
                      ...chipStyles,
                      ...styles.resultChip,
                    }}
                  >
                    {item?.description}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {!hasSelectableOptions && !!correctDescriptions.length && (
          <div style={styles.questionSection}>
            <div style={styles.questionSectionTitle}>
              Respostas corretas esperadas
            </div>
            <div style={styles.chipsRow}>
              {correctDescriptions.map((item, index) => (
                <span
                  key={`${question?.id || idx}-correct-${index}`}
                  style={{
                    background: evaluation.hasMultipleCorrect ? '#FFFBEB' : '#ECFDF5',
                    border: evaluation.hasMultipleCorrect
                      ? '1px solid #FDE68A'
                      : '1px solid #A7F3D0',
                    color: evaluation.hasMultipleCorrect ? '#B45309' : '#047857',
                    ...styles.resultChip,
                  }}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {hasAttachments && (
          <div style={styles.questionSection}>
            <div style={styles.questionSectionTitle}>
              Midias e anexos
            </div>
            {renderMediaGrid(question.attachments)}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      role="presentation"
      style={styles.modalOverlay}
      onMouseDown={() => {
        if (lightbox.isOpen || preview.isOpen) return;
        onClose();
      }}
    >
      {lightbox.isOpen && (lightbox.items?.length || 0) > 0 && (
        <Lightbox
          mainSrc={lightbox.items[lightbox.index].url}
          nextSrc={
            lightbox.items[(lightbox.index + 1) % lightbox.items.length].url
          }
          prevSrc={
            lightbox.items[
              (lightbox.index + lightbox.items.length - 1) %
                lightbox.items.length
            ].url
          }
          onCloseRequest={() =>
            setLightbox(prev => ({ ...prev, isOpen: false }))
          }
          onMovePrevRequest={() =>
            setLightbox(prev => ({
              ...prev,
              index:
                (prev.index + prev.items.length - 1) % prev.items.length,
            }))
          }
          onMoveNextRequest={() =>
            setLightbox(prev => ({
              ...prev,
              index: (prev.index + 1) % prev.items.length,
            }))
          }
          imageTitle={lightbox.items[lightbox.index].name}
          reactModalStyle={{
            overlay: { zIndex: 10003 },
          }}
        />
      )}

      {preview.isOpen && (
        <div
          style={styles.previewOverlay}
          onMouseDown={event => {
            if (event.target === event.currentTarget) closePreview();
          }}
        >
          <div
            style={styles.previewDialog}
            onMouseDown={event => event.stopPropagation()}
          >
            <div style={styles.previewHeader}>
              <div style={styles.previewNameWrap}>
                <div
                  style={styles.previewName}
                  title={preview.name}
                >
                  {preview.name || 'Arquivo'}
                </div>
              </div>

              <div style={styles.previewActions}>
                {preview.kind === 'video' && (
                  <button
                    type="button"
                    style={styles.previewDownloadButton}
                    title="Tela cheia"
                    onClick={openPreviewFullscreen}
                  >
                    ⛶
                  </button>
                )}

                <button
                  type="button"
                  style={styles.previewDownloadButton}
                  title="Baixar"
                  onClick={() => downloadFile(preview.url, preview.name)}
                >
                  <DownloadOutlinedIcon style={{ color: '#111827', fontSize: 20 }} />
                </button>

                <button
                  type="button"
                  onClick={closePreview}
                  style={styles.previewCloseButton}
                >
                  ✕
                </button>
              </div>
            </div>

            <div style={styles.previewBody}>
              {preview.loading ? (
                <div style={styles.previewLoading}>Carregando...</div>
              ) : preview.error ? (
                <div style={styles.previewInfoWrap}>
                  <div style={styles.previewError}>{preview.error}</div>
                </div>
              ) : preview.kind === 'pdf' ? (
                <object
                  data={preview.blobUrl}
                  type="application/pdf"
                  style={styles.previewFrame}
                >
                  <div style={styles.previewInfoWrap}>
                    <div style={styles.previewInfoText}>
                      Não foi possível exibir o PDF nesta visualização.
                    </div>
                  </div>
                </object>
              ) : preview.kind === 'video' ? (
                <div style={styles.previewVideoWrap}>
                  <video
                    ref={previewVideoRef}
                    src={preview.url}
                    controls
                    playsInline
                    style={styles.previewVideo}
                  />
                </div>
              ) : (
                <div style={styles.previewInfoWrap}>
                  <div style={styles.previewInfoText}>
                    Não é possível pré-visualizar este tipo de arquivo aqui.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        role="presentation"
        style={styles.modalDialog}
        onMouseDown={event => event.stopPropagation()}
      >
        <div style={styles.modalHeader}>
          <div style={styles.modalHeaderRow}>
              <div>
                <div style={styles.modalTitle}>{title}</div>
                <div style={styles.modalDivider} />
                <div style={styles.infoPillsRow}>
                {infoPills.map((item, idx) => (
                  <div key={idx} style={styles.infoPill}>
                    {item.icon}
                    {item.label ? (
                      <span>
                        {item.label}:{' '}
                        <strong style={styles.infoPillStrong}>{item.value}</strong>
                      </span>
                    ) : (
                      <span>{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={styles.closeButton}
            >
              <CloseOutlinedIcon style={{ fontSize: 32 }} />
            </button>
          </div>
        </div>

        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <SearchOutlinedIcon style={{ color: '#9CA3AF' }} />
            <input
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Buscar por CPF, nome, e-mail ou telefone"
              style={styles.searchInput}
            />
          </div>

          <button
            type="button"
            onClick={() => toggleFilter('active')}
            style={styles.filterButton}
          >
            <span
              style={{
                ...styles.filterBox,
                background: filters.active ? '#60A5FA' : '#FFFFFF',
              }}
            >
              {filters.active ? <CheckOutlinedIcon style={{ fontSize: 18 }} /> : null}
            </span>
            Treinamento ativo
          </button>

          <button
            type="button"
            onClick={() => toggleFilter('inactive')}
            style={styles.filterButton}
          >
            <span
              style={{
                ...styles.filterBox,
                background: filters.inactive ? '#60A5FA' : '#FFFFFF',
              }}
            >
              {filters.inactive ? <CheckOutlinedIcon style={{ fontSize: 18 }} /> : null}
            </span>
            Treinamento vencido
          </button>

          <button
            type="button"
            onClick={() => setAllExpanded(true)}
            style={styles.toolbarAction}
          >
            <UnfoldMoreOutlinedIcon />
            {allExpanded ? 'Todas expandidas' : 'Expandir todas'}
          </button>

          <button
            type="button"
            onClick={() => setAllExpanded(false)}
            style={styles.toolbarAction}
          >
            <UnfoldLessOutlinedIcon />
            Recolher todas
          </button>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.centeredMessage}>
              Carregando respostas...
            </div>
          ) : filteredResponses.length === 0 ? (
            <div style={styles.centeredMessage}>
              Nenhuma resposta encontrada.
            </div>
          ) : (
            filteredResponses.map((responseItem, index) => {
              const responseKey = getResponseKey(responseItem, index);
              const identity = getResponseIdentity(responseItem);
              const score = getComputedResponseScore(responseItem);
              const certificateUrl = getCertificateUrl(responseItem);
              const showCertificateButton =
                !!certificateUrl || isCertificateEligible(score);
              const statusMeta = buildStatusMeta(responseItem);
              const scoreMeta = getScoreMeta(score);
              const isExpanded = !!expandedMap[responseKey];
              const infoCards = [
                buildInfoCard(
                  <PersonOutlineOutlinedIcon style={{ fontSize: 18, color: '#8B8B8B' }} />,
                  'Nome',
                  identity.name
                ),
                buildInfoCard(
                  <EmailOutlinedIcon style={{ fontSize: 18, color: '#8B8B8B' }} />,
                  'Email',
                  identity.email
                ),
                buildInfoCard(
                  <PhoneOutlinedIcon style={{ fontSize: 18, color: '#8B8B8B' }} />,
                  'Telefone',
                  identity.phone
                ),
                buildInfoCard(
                  <BadgeOutlinedIcon style={{ fontSize: 18, color: '#8B8B8B' }} />,
                  'CPF',
                  identity.cpf
                ),
                buildInfoCard(
                  <CalendarTodayOutlinedIcon style={{ fontSize: 18, color: '#8B8B8B' }} />,
                  'Data e Hora',
                  formatDateTimeBR(responseItem?.answeredAt)
                ),
              ];

              return (
                <div key={responseKey} style={styles.responseCard}>
                  <div style={styles.responseHeader}>
                    <div style={styles.responseHeaderLeft}>
                      <div style={styles.responseTitle}>
                        Resposta #{index + 1}
                      </div>

                      <span
                        style={{
                          height: 28,
                          padding: '0 12px',
                          borderRadius: 12,
                          background: scoreMeta.background,
                          border: scoreMeta.border,
                          color: scoreMeta.color,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span
                          style={{
                            ...styles.scoreAccent,
                            background: scoreMeta.accent,
                          }}
                        />
                        {Number(score?.totalAnswers || 0) === 0
                          ? `Treinamento concluído • Total de perguntas respondidas: ${formatScoreValue(
                              score?.answeredPercentage
                            )}%`
                          : `Pontuação: ${formatScoreValue(
                              score?.percentage
                            )}% • Acertos: ${formatScoreValue(
                              score?.correctAnswers
                            )}/${formatScoreValue(
                              score?.totalAnswers
                            )} • Total de perguntas respondidas: ${formatScoreValue(
                              score?.answeredPercentage
                            )}%`}
                      </span>

                      <span
                        style={{
                          ...statusMeta.styles,
                          height: 28,
                          padding: '0 12px',
                          borderRadius: 10,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12,
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span
                          style={{
                            ...styles.scoreAccent,
                            background: statusMeta.styles.color,
                          }}
                        />
                        {statusMeta.label}
                      </span>
                    </div>

                    <div style={styles.responseActions}>
                      <button
                        type="button"
                        onClick={() => toggleExpand(responseKey)}
                        style={styles.primaryActionButton}
                      >
                        <VisibilityOutlinedIcon style={{ fontSize: 20 }} />
                        {isExpanded ? 'Ocultar' : 'Visualizar'}
                      </button>

                      <button
                        type="button"
                        onClick={() => handleExport(responseItem, index)}
                        disabled={exportingId === String(responseItem?.id ?? index)}
                        style={styles.secondaryActionButton}
                      >
                        <DownloadOutlinedIcon style={{ fontSize: 20 }} />
                        {exportingId === String(responseItem?.id ?? index)
                          ? 'Exportando...'
                          : 'Exportar'}
                      </button>

                      {showCertificateButton && (
                        <button
                          type="button"
                          onClick={() => handleDownloadCertificate(responseItem)}
                          disabled={!certificateUrl}
                          title={
                            certificateUrl
                              ? 'Baixar certificado'
                              : 'Certificado não disponível no retorno da API'
                          }
                          style={{
                            ...styles.secondaryActionButton,
                            opacity: certificateUrl ? 1 : 0.55,
                            cursor: certificateUrl ? 'pointer' : 'not-allowed',
                          }}
                        >
                          <CardMembershipOutlinedIcon style={{ fontSize: 20 }} />
                          Certificado
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={styles.responseDivider} />

                  <div style={styles.responseInfoGrid}>
                    {infoCards.map((card, cardIndex) => (
                      <div key={cardIndex} style={styles.responseInfoCard}>
                        <div style={styles.responseInfoLabel}>
                          {card.icon}
                          {card.label}
                        </div>
                        <div style={styles.responseInfoValue}>
                          {card.value}
                        </div>
                      </div>
                    ))}
                  </div>

                  {isExpanded && (
                    <div style={styles.expandedSection}>
                      <div style={styles.questionsGrid}>
                        {getGroupQuestions(responseItem).map((question, idx) =>
                          renderQuestionCard(question, idx)
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
